import { useSyncExternalStore } from "react";
import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import i18n from "./config";
import {
  type Lang,
  DEFAULT_LANG,
  localizePath,
  subscribeBlogSlugs,
  getBlogSlugVersion,
  parsePath,
  LANG_HREFLANG,
} from "./locales";

interface LocaleContextValue {
  lang: Lang;
}

const LocaleContext = createContext<LocaleContextValue>({ lang: DEFAULT_LANG });

/**
 * Wraps a localized branch of the route tree. Sets the active i18next language
 * and the <html lang> attribute, and exposes the language via context.
 */
export const LocaleLayout = ({
  lang,
  children,
}: {
  lang: Lang;
  children: ReactNode;
}) => {
  useEffect(() => {
    if (i18n.language !== lang) i18n.changeLanguage(lang);
    document.documentElement.lang = LANG_HREFLANG[lang];
  }, [lang]);

  return (
    <LocaleContext.Provider value={{ lang }}>{children}</LocaleContext.Provider>
  );
};

export function useLocale() {
  const { lang } = useContext(LocaleContext);
  const navigate = useNavigate();
  const location = useLocation();
  // Re-render consumers once localized blog slugs have loaded.
  useSyncExternalStore(subscribeBlogSlugs, getBlogSlugVersion, getBlogSlugVersion);

  /** Build a prefixed href for a logical (un-prefixed) path in the current language. */
  const localize = (path: string) => localizePath(path, lang);

  /** Switch to another language, staying on the equivalent page. */
  const switchLanguage = (next: Lang) => {
    const { path } = parsePath(location.pathname);
    try {
      localStorage.setItem("devcore_lang", next);
    } catch {
      /* ignore storage failures */
    }
    navigate(localizePath(path, next) + location.hash, {
      state: { preserveScroll: true },
    });
  };

  return { lang, localize, switchLanguage };
}
